import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, onValue, set } from "firebase/database";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";
import { onAuthStateChanged } from "firebase/auth";

const Checkout = () => {
    const [cartItems, setCartItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]); // Menyimpan produk yang dipilih
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                const userId = user.uid;
                const db = getDatabase();
                const cartRef = ref(db, `cart/${userId}`);

                const unsubscribeCart = onValue(cartRef, (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        const items = Object.values(data).map(
                            (item, index) => ({
                                ...item,
                                id: Object.keys(data)[index]
                            })
                        );
                        setCartItems(items);
                    } else {
                        setCartItems([]);
                    }
                });

                return () => unsubscribeCart(); // Hapus listener saat komponen tidak digunakan
            }
        });

        return () => unsubscribe(); // Hapus listener autentikasi saat komponen tidak digunakan
    }, []); // Kosongkan dependensi untuk menghindari update berulang

    useEffect(() => {
        const total = cartItems
            .filter((item) => selectedItems.includes(item.id))
            .reduce((acc, item) => acc + item.price * item.count, 0);
        setTotalPrice(total);
    }, [cartItems, selectedItems]);

    const increaseQuantity = (itemId) => {
        setCartItems((prevItems) =>
            prevItems.map((item) =>
                item.id === itemId ? { ...item, count: item.count + 1 } : item
            )
        );
    };

    const decreaseQuantity = (itemId) => {
        setCartItems((prevItems) =>
            prevItems.map((item) =>
                item.id === itemId && item.count > 1
                    ? { ...item, count: item.count - 1 }
                    : item
            )
        );
    };

    const handleDeleteItem = (itemId) => {
        const userId = auth.currentUser?.uid;
        if (userId) {
            const db = getDatabase();
            const cartRef = ref(db, `cart/${userId}/${itemId}`);
            set(cartRef, null).then(() => {
                toast.success("Produk berhasil dihapus!");
            });
        }
    };

    const handleCheckout = () => {
        const userId = auth.currentUser?.uid;
        const orderId = "order_" + Math.random().toString(36).substr(2, 9);

        if (userId) {
            const db = getDatabase();
            const userRef = ref(db, `users/${userId}`);
            const orderRef = ref(db, `orders/${userId}/${orderId}`);

            const selectedCartItems = cartItems.filter((item) =>
                selectedItems.includes(item.id)
            );

            if (selectedCartItems.length === 0) {
                toast.error(
                    "Silakan pilih minimal satu produk untuk checkout!"
                );
                return;
            }

            const totalNewPrice = selectedCartItems.reduce(
                (acc, item) => acc + item.price * item.count,
                0
            );

            // Cek saldo pengguna
            get(userRef).then((snapshot) => {
                const userData = snapshot.val();
                const userSaldo = userData?.saldo || 0;

                if (userSaldo < totalNewPrice) {
                    toast.error("Saldo Anda tidak mencukupi!");
                    return;
                }

                const updatedSaldo = userSaldo - totalNewPrice;

                // Menambahkan pesan admin dari produk
                const orderData = {
                    items: selectedCartItems.map((item) => ({
                        ...item,
                        adminMessage:
                            item.adminMessage || "Pesan tidak tersedia"
                    })),
                    totalPrice: totalNewPrice,
                    date: Date.now(),
                    status: "pending"
                };

                set(orderRef, orderData)
                    .then(() => {
                        set(userRef, { ...userData, saldo: updatedSaldo });
                        const cartRef = ref(db, `cart/${userId}`);
                        set(cartRef, {});
                        toast.success("Pesanan berhasil dibuat!");
                        navigate("/");
                    })
                    .catch((error) => {
                        toast.error(
                            "Terjadi kesalahan saat memproses pesanan."
                        );
                        console.error(error);
                    });
            });
        }
    };

    const handleSelectItem = (itemId) => {
        if (selectedItems.includes(itemId)) {
            setSelectedItems(selectedItems.filter((id) => id !== itemId));
        } else {
            setSelectedItems([...selectedItems, itemId]);
        }
    };

    const handleSelectAll = () => {
        if (selectedItems.length === cartItems.length) {
            setSelectedItems([]); // Kosongkan jika semua sudah terpilih
        } else {
            setSelectedItems(cartItems.map((item) => item.id));
        }
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-4">Checkout</h1>
            <div className="grid grid-cols-1 gap-6">
                {cartItems.length === 0 ? (
                    <p>Keranjang Anda kosong.</p>
                ) : (
                    <>
                        {/* Checkbox Pilih Semua */}
                        <div className="flex items-center mb-4">
                            <input
                                type="checkbox"
                                checked={
                                    selectedItems.length === cartItems.length
                                }
                                onChange={handleSelectAll}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium">
                                Pilih Semua
                            </span>
                        </div>

                        {cartItems.map((item) => (
                            <div
                                key={item.id}
                                className="flex items-center border-b pb-4 mb-4"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedItems.includes(item.id)}
                                    onChange={() => handleSelectItem(item.id)}
                                    className="mr-4"
                                />
                                <img
                                    src={item.images[0].value}
                                    alt={item.name}
                                    className="w-16 h-16 rounded-lg object-cover mr-4"
                                />
                                <div className="flex-1">
                                    <p className="font-semibold">{item.name}</p>
                                    <p className="text-sm text-gray-500">
                                        Rp{" "}
                                        {Number(item.price).toLocaleString(
                                            "id-ID"
                                        )}
                                    </p>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() =>
                                            decreaseQuantity(item.id)
                                        }
                                        className="p-2 border rounded bg-gray-200"
                                    >
                                        -
                                    </button>
                                    <span className="w-8 text-center">
                                        {item.count}
                                    </span>
                                    <button
                                        onClick={() =>
                                            increaseQuantity(item.id)
                                        }
                                        className="p-2 border rounded bg-gray-200"
                                    >
                                        +
                                    </button>
                                </div>
                                <button
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="ml-4 text-red-500 hover:text-red-700"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        ))}

                        <div className="mt-6 flex justify-between items-center">
                            <p className="text-xl font-semibold">
                                Total: Rp {totalPrice.toLocaleString("id-ID")}
                            </p>
                            <button
                                onClick={handleCheckout}
                                className="btn btn-primary"
                            >
                                Lanjutkan Pembayaran
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Checkout;
